import React, { useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Button,
  Spinner,
  Text,
  SimpleGrid,
  useDisclosure,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Badge,
  Portal,
} from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  DotsThree,
  PencilSimple,
  Trash,
  Kanban,
  Columns,
  ArrowRight,
  CaretDown,
  Buildings,
} from 'phosphor-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PageBox } from '../../components/pageBox';
import { useKanban, IKanbanProject, IKanbanBoard, IKanbanResponse } from '../../hooks/useKanban';
import { useDepartamentos, IDepartamento } from '../../hooks/useDepartamentos';
import { useAuth } from '../../providers/auth';
import { KanbanProjectModal } from '../../components/kanban/KanbanProjectModal';
import DialogModal from '../../components/DialogModal';
import { IDataNoPagination } from '../../types/shared';
import { rotuloDoModuloDeOrigem } from '../../components/kanban/modulosDeOrigem';

const GRUPOS_FILTRO_LIVRE = [1, 3];

const GRADIENTS = [
  'linear(to-r, #6366F1, #8B5CF6)',
  'linear(to-r, #EC4899, #F43F5E)',
  'linear(to-r, #F97316, #EAB308)',
  'linear(to-r, #22C55E, #14B8A6)',
  'linear(to-r, #06B6D4, #3B82F6)',
  'linear(to-r, #A855F7, #6366F1)',
  'linear(to-r, #10B981, #06B6D4)',
  'linear(to-r, #F43F5E, #F97316)',
  'linear(to-r, #3B82F6, #8B5CF6)',
  'linear(to-r, #14B8A6, #22C55E)',
  'linear(to-r, #EAB308, #F97316)',
  'linear(to-r, #8B5CF6, #EC4899)',
];

const BG_LIGHTS = [
  '#EEF2FF',
  '#FDF2F8',
  '#FFF7ED',
  '#ECFDF5',
  '#ECFEFF',
  '#F5F3FF',
  '#ECFDF5',
  '#FEF2F2',
  '#EFF6FF',
  '#F0FDFA',
  '#FEFCE8',
  '#FAF5FF',
];

const getProjectStyle = (name?: string | null) => {
  if (!name) return { gradient: GRADIENTS[0], bg: BG_LIGHTS[0] };
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = name.charCodeAt(i) + hash * 31;
  }
  const index = Math.abs(hash) % GRADIENTS.length;
  return { gradient: GRADIENTS[index], bg: BG_LIGHTS[index] };
};

export const KanbanProjectsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { getProjects, createProject, updateProject, archiveProject, getBoard } = useKanban();
  const { getDepartamentos } = useDepartamentos();

  const [projectToEdit, setProjectToEdit] = useState<IKanbanProject | null>(null);
  const [projectToArchive, setProjectToArchive] = useState<number | null>(null);
  // null = todos; 'eventos' = apenas projetos de evento; number = id do departamento real
  const [selectedFiltro, setSelectedFiltro] = useState<number | 'eventos' | null>(null);

  const {
    isOpen: isProjectModalOpen,
    onOpen: onProjectModalOpen,
    onClose: onProjectModalClose,
  } = useDisclosure();

  const {
    isOpen: isDeleteDialogOpen,
    onOpen: onDeleteDialogOpen,
    onClose: onDeleteDialogClose,
  } = useDisclosure();

  const grupoId = Number(user?.grupo_id);
  const isAdminKanban = [1, 3, 5].includes(grupoId); // Admin, Diretoria, Dev
  const isGestor = grupoId === 4;
  const canManageProjects = isAdminKanban || isGestor;
  const canFiltrarTodosDepartamentos = GRUPOS_FILTRO_LIVRE.includes(grupoId);
  const userDepartamentos: { id: number; nome: string }[] = (user as any)?.departamentos ?? [];
  const canFiltrarDepartamento = canFiltrarTodosDepartamentos || userDepartamentos.length > 1;

  const apenasEventos = selectedFiltro === 'eventos';
  const departamentoIdFiltro = typeof selectedFiltro === 'number' ? selectedFiltro : null;

  const { data: projectsData, isLoading } = useQuery<IKanbanResponse<IKanbanProject[]>>({
    queryKey: [
      'kanban-projects',
      { departamento_id: departamentoIdFiltro, apenas_eventos: apenasEventos },
    ],
    queryFn: getProjects,
  });

  const { data: departamentosData } = useQuery<IDataNoPagination<IDepartamento[]>>({
    queryKey: ['departamentos-filtro-projetos'],
    queryFn: getDepartamentos,
    enabled: canFiltrarTodosDepartamentos,
  });
  // Admin/Gestor/Diretoria: todos os departamentos do sistema; demais: só os seus
  const departamentos = canFiltrarTodosDepartamentos
    ? departamentosData?.data || []
    : userDepartamentos;
  const selectedDepartamento =
    typeof selectedFiltro === 'number'
      ? departamentos.find(d => d.id === selectedFiltro) || null
      : null;

  const filtroLabel = (() => {
    if (selectedFiltro === 'eventos') return 'Eventos';
    if (selectedDepartamento) return selectedDepartamento.nome;
    return 'Todos os departamentos';
  })();

  const { data: boardData } = useQuery<IKanbanResponse<IKanbanBoard>>({
    queryKey: ['kanban-board-columns', projectToEdit?.id],
    queryFn: () => getBoard(projectToEdit!.id),
    enabled: !!projectToEdit,
  });

  const projects = projectsData?.data || [];
  // Task #757: separa o /kanban em 2 faixas — quadros avulsos (editáveis) no topo
  // e quadros de módulo (Estatutos/Eventos), espelho read-only, embaixo.
  const projetosAvulsos = projects.filter(p => p.origem_modulo == null);
  const projetosModulo = projects.filter(p => p.origem_modulo != null);

  const createProjectMutation = useMutation({
    mutationFn: createProject,
    onSuccess: response => {
      queryClient.invalidateQueries({ queryKey: ['kanban-projects'] });
      toast.success('Projeto criado!');
      navigate(`/kanban/${response.data.id}`);
    },
    onError: () => toast.error('Erro ao criar projeto'),
  });

  const updateProjectMutation = useMutation({
    mutationFn: updateProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-projects'] });
      toast.success('Projeto atualizado!');
    },
    onError: () => toast.error('Erro ao atualizar projeto'),
  });

  const archiveProjectMutation = useMutation({
    mutationFn: archiveProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-projects'] });
      toast.success('Projeto arquivado!');
      setProjectToArchive(null);
    },
    onError: () => toast.error('Erro ao arquivar projeto'),
  });

  const handleCreateProject = () => {
    setProjectToEdit(null);
    onProjectModalOpen();
  };

  const handleEditProject = (e: React.MouseEvent, project: IKanbanProject) => {
    e.stopPropagation();
    setProjectToEdit(project);
    onProjectModalOpen();
  };

  const handleArchiveProject = (e: React.MouseEvent, projectId: number) => {
    e.stopPropagation();
    setProjectToArchive(projectId);
    onDeleteDialogOpen();
  };

  const confirmArchiveProject = () => {
    if (projectToArchive) {
      archiveProjectMutation.mutate(projectToArchive);
    }
    onDeleteDialogClose();
  };

  const handleSaveProject = (
    nome: string,
    descricao: string,
    departamentoIds: number[],
    projectId?: number,
    colunaFinalizadoId?: number | null,
    cor?: string | null,
  ) => {
    if (projectId) {
      updateProjectMutation.mutate({
        id: projectId,
        nome,
        descricao,
        cor,
        departamento_ids: departamentoIds,
        coluna_finalizado_id: colunaFinalizadoId,
      });
    } else {
      createProjectMutation.mutate({
        titulo: nome,
        descricao,
        cor,
        departamento_ids: departamentoIds,
      });
    }
  };

  const handleProjectClick = (project: IKanbanProject) => {
    navigate(`/kanban/${project.id}`);
  };

  // Rótulo amigável do módulo p/ o badge da faixa de baixo.
  // Rótulo do módulo de origem: vem do registry (`components/kanban/modulosDeOrigem`),
  // o mesmo que o board usa pra resolver a rota do botão "Abrir no módulo".
  // ⚠️ A versão local que existia aqui não conhecia `lgz_*`, então board de
  // legalização aparecia como "Módulo" genérico enquanto o botão dizia
  // Legalização — duas leituras do mesmo campo, discordando.
  const moduloLabel = rotuloDoModuloDeOrigem;

  // Card de projeto reutilizado nas duas faixas. `readOnly` = quadro de módulo:
  // sem menu de editar/arquivar (a gestão é feita na tela do módulo de origem).
  const renderProjectCard = (project: IKanbanProject, readOnly: boolean) => {
    const projectName = project.nome || project.titulo || 'Sem nome';
    const style = getProjectStyle(projectName);
    const projectColor = project.cor || null;
    const depts = project.departamentos || [];
    const isEvento = project.evento_id != null;
    const showMenu = canManageProjects && !readOnly;

    return (
      <Box
        key={project.id}
        bg="white"
        borderRadius="16px"
        cursor="pointer"
        transition="all 0.2s ease"
        border="1px solid"
        borderColor="gray.100"
        overflow="hidden"
        _hover={{
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
          borderColor: 'gray.200',
        }}
        onClick={() => handleProjectClick(project)}
      >
        {/* Color top bar */}
        <Box
          h="3px"
          bg={projectColor || undefined}
          bgGradient={projectColor ? undefined : style.gradient}
        />

        <Box p={5}>
          {/* Title + Menu */}
          <Flex justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Flex alignItems="center" gap={2.5} flex={1} pr={2}>
              <Flex
                w="36px"
                h="36px"
                borderRadius="10px"
                bg={style.bg}
                align="center"
                justify="center"
                flexShrink={0}
              >
                <Columns
                  size={18}
                  color={style.gradient.includes('#6366F1') ? '#6366F1' : '#3B82F6'}
                  weight="bold"
                />
              </Flex>
              <Text
                fontWeight="600"
                fontSize="14px"
                color="gray.800"
                noOfLines={1}
                lineHeight="1.3"
              >
                {projectName}
              </Text>
            </Flex>
            {showMenu && (
              <Menu>
                <MenuButton
                  as={IconButton}
                  icon={<DotsThree size={18} weight="bold" />}
                  variant="ghost"
                  size="xs"
                  minW="28px"
                  h="28px"
                  borderRadius="lg"
                  aria-label="Opcoes"
                  onClick={e => e.stopPropagation()}
                  color="gray.400"
                  _hover={{ bg: 'gray.100', color: 'gray.600' }}
                />
                <Portal>
                  <MenuList
                    borderRadius="12px"
                    boxShadow="lg"
                    border="1px solid"
                    borderColor="gray.100"
                    py={1}
                    minW="150px"
                  >
                    <MenuItem
                      icon={<PencilSimple size={14} />}
                      onClick={e => handleEditProject(e, project)}
                      fontSize="13px"
                      py={2}
                      borderRadius="lg"
                      mx={1}
                      _hover={{ bg: 'gray.50' }}
                    >
                      Editar
                    </MenuItem>
                    <MenuItem
                      icon={<Trash size={14} />}
                      color="red.500"
                      onClick={e => handleArchiveProject(e, project.id)}
                      fontSize="13px"
                      py={2}
                      borderRadius="lg"
                      mx={1}
                      _hover={{ bg: 'red.50' }}
                    >
                      Arquivar
                    </MenuItem>
                  </MenuList>
                </Portal>
              </Menu>
            )}
          </Flex>

          {/* Description */}
          {project.descricao && (
            <Text fontSize="12px" color="gray.400" noOfLines={2} mb={3} pl="46px">
              {project.descricao}
            </Text>
          )}

          {/* Departments / Module badge + Arrow */}
          <Flex justifyContent="space-between" alignItems="center" pl="46px">
            <Flex gap={1.5} flexWrap="wrap" flex={1}>
              {readOnly && (
                <Badge
                  fontSize="10px"
                  fontWeight="600"
                  color="blue.600"
                  bg="blue.50"
                  px={2}
                  py={0.5}
                  borderRadius="md"
                  textTransform="none"
                >
                  {moduloLabel(project.origem_modulo)}
                </Badge>
              )}
              {!readOnly && isEvento && (
                <Badge
                  fontSize="10px"
                  fontWeight="600"
                  color="purple.600"
                  bg="purple.50"
                  px={2}
                  py={0.5}
                  borderRadius="md"
                  textTransform="none"
                >
                  Eventos
                </Badge>
              )}
              {!readOnly && !isEvento && (
                <>
                  {depts.slice(0, 3).map(d => (
                    <Badge
                      key={d.id}
                      fontSize="10px"
                      fontWeight="500"
                      color="gray.500"
                      bg="gray.50"
                      px={2}
                      py={0.5}
                      borderRadius="md"
                      textTransform="none"
                    >
                      {d.nome}
                    </Badge>
                  ))}
                  {depts.length > 3 && (
                    <Badge
                      fontSize="10px"
                      fontWeight="500"
                      color="gray.400"
                      bg="gray.50"
                      px={2}
                      py={0.5}
                      borderRadius="md"
                      textTransform="none"
                    >
                      +{depts.length - 3}
                    </Badge>
                  )}
                  {depts.length === 0 && !project.descricao && (
                    <Text fontSize="11px" color="gray.300">
                      Sem departamentos
                    </Text>
                  )}
                </>
              )}
            </Flex>
            <Flex
              w="28px"
              h="28px"
              borderRadius="full"
              align="center"
              justify="center"
              bg="gray.50"
              flexShrink={0}
            >
              <ArrowRight size={14} color="#9CA3AF" weight="bold" />
            </Flex>
          </Flex>
        </Box>
      </Box>
    );
  };

  if (isLoading) {
    return (
      <PageBox>
        <Flex justify="center" align="center" minH="100vh" bg="white">
          <Spinner size="lg" color="gray.400" thickness="2px" />
        </Flex>
      </PageBox>
    );
  }

  return (
    <PageBox>
      <Box bg="gray.50" minH="100vh">
        {/* Header */}
        <Box bg="white" borderBottom="1px solid" borderColor="gray.100">
          <Box px={{ base: 6, md: 10 }} py={6}>
            <Flex alignItems="center" gap={3} justifyContent="space-between" flexWrap="wrap">
              <Flex alignItems="center" gap={3}>
                <Flex
                  w="40px"
                  h="40px"
                  borderRadius="12px"
                  bgGradient="linear(to-br, gray.800, gray.900)"
                  align="center"
                  justify="center"
                >
                  <Kanban size={20} color="white" weight="bold" />
                </Flex>
                <Box>
                  <Heading
                    fontSize={{ base: 'lg', md: 'xl' }}
                    fontWeight="700"
                    color="gray.900"
                    letterSpacing="-0.02em"
                  >
                    Projetos
                  </Heading>
                  <Text fontSize="12px" color="gray.400" fontWeight="500">
                    {projects.length} {projects.length === 1 ? 'projeto' : 'projetos'}
                  </Text>
                </Box>
              </Flex>

              {canFiltrarDepartamento && (
                <Menu>
                  <MenuButton
                    as={Button}
                    leftIcon={<Buildings size={16} weight="bold" />}
                    rightIcon={<CaretDown size={14} weight="bold" />}
                    variant="outline"
                    size="sm"
                    borderRadius="full"
                    px={4}
                    fontWeight="500"
                    fontSize="13px"
                    color="gray.700"
                    borderColor="gray.200"
                    bg="white"
                    _hover={{ bg: 'gray.50', borderColor: 'gray.300' }}
                    _active={{ bg: 'gray.50' }}
                  >
                    {filtroLabel}
                  </MenuButton>
                  <Portal>
                    <MenuList
                      borderRadius="12px"
                      boxShadow="lg"
                      border="1px solid"
                      borderColor="gray.100"
                      py={1}
                      minW="240px"
                      maxH="320px"
                      overflowY="auto"
                      overflowX="hidden"
                    >
                      <MenuItem
                        onClick={() => setSelectedFiltro(null)}
                        fontSize="13px"
                        fontWeight={selectedFiltro === null ? '600' : '500'}
                        color={selectedFiltro === null ? 'gray.900' : 'gray.600'}
                        py={2}
                        borderRadius="lg"
                        mx={1}
                        _hover={{ bg: 'gray.50' }}
                      >
                        Todos os departamentos
                      </MenuItem>
                      <MenuItem
                        onClick={() => setSelectedFiltro('eventos')}
                        fontSize="13px"
                        fontWeight={selectedFiltro === 'eventos' ? '600' : '500'}
                        color={selectedFiltro === 'eventos' ? 'gray.900' : 'gray.600'}
                        py={2}
                        borderRadius="lg"
                        mx={1}
                        _hover={{ bg: 'gray.50' }}
                      >
                        Eventos
                      </MenuItem>
                      {departamentos.map(d => (
                        <MenuItem
                          key={d.id}
                          onClick={() => setSelectedFiltro(d.id)}
                          fontSize="13px"
                          fontWeight={selectedFiltro === d.id ? '600' : '500'}
                          color={selectedFiltro === d.id ? 'gray.900' : 'gray.600'}
                          py={2}
                          borderRadius="lg"
                          mx={1}
                          _hover={{ bg: 'gray.50' }}
                        >
                          {d.nome}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Portal>
                </Menu>
              )}
            </Flex>
          </Box>
        </Box>

        {/* Projects */}
        <Box px={{ base: 6, md: 10 }} py={8}>
          {projects.length === 0 ? (
            <Flex justify="center" align="center" minH="50vh" flexDirection="column" gap={5}>
              <Flex
                w="72px"
                h="72px"
                borderRadius="20px"
                bg="white"
                border="2px dashed"
                borderColor="gray.200"
                align="center"
                justify="center"
              >
                <Kanban size={32} color="#9CA3AF" weight="light" />
              </Flex>
              <Box textAlign="center">
                <Text fontSize="md" fontWeight="600" color="gray.700" mb={1}>
                  Nenhum projeto ainda
                </Text>
                <Text color="gray.400" fontSize="sm" maxW="300px">
                  Crie seu primeiro projeto para organizar tarefas em quadros Kanban
                </Text>
              </Box>
              {canManageProjects && (
                <Button
                  leftIcon={<Plus size={15} weight="bold" />}
                  bg="gray.900"
                  color="white"
                  size="sm"
                  borderRadius="full"
                  px={5}
                  mt={1}
                  fontWeight="500"
                  _hover={{ bg: 'gray.800' }}
                  onClick={handleCreateProject}
                >
                  Criar projeto
                </Button>
              )}
            </Flex>
          ) : (
            <Flex direction="column" gap={10}>
              {/* Faixa 1 — Quadros avulsos (editáveis) */}
              <Box>
                {projetosModulo.length > 0 && (
                  <Text
                    fontSize="12px"
                    fontWeight="700"
                    color="gray.500"
                    textTransform="uppercase"
                    letterSpacing="0.04em"
                    mb={3}
                  >
                    Quadros avulsos
                  </Text>
                )}
                <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing={5}>
                  {projetosAvulsos.map(project => renderProjectCard(project, false))}

                  {/* Add project card */}
                  {canManageProjects && (
                    <Flex
                      bg="white"
                      borderRadius="16px"
                      border="2px dashed"
                      borderColor="gray.200"
                      cursor="pointer"
                      transition="all 0.2s ease"
                      align="center"
                      justify="center"
                      minH="120px"
                      direction="column"
                      gap={2}
                      _hover={{
                        borderColor: 'gray.300',
                        bg: 'gray.50',
                      }}
                      onClick={handleCreateProject}
                    >
                      <Flex
                        w="36px"
                        h="36px"
                        borderRadius="full"
                        bg="gray.100"
                        align="center"
                        justify="center"
                      >
                        <Plus size={16} color="#9CA3AF" weight="bold" />
                      </Flex>
                      <Text fontSize="13px" color="gray.400" fontWeight="500">
                        Novo projeto
                      </Text>
                    </Flex>
                  )}
                </SimpleGrid>
              </Box>

              {/* Faixa 2 — Quadros de módulo (espelho read-only) */}
              {projetosModulo.length > 0 && (
                <Box>
                  <Flex alignItems="center" gap={2} mb={1}>
                    <Text
                      fontSize="12px"
                      fontWeight="700"
                      color="gray.500"
                      textTransform="uppercase"
                      letterSpacing="0.04em"
                    >
                      Quadros de módulos
                    </Text>
                    <Badge
                      fontSize="9px"
                      fontWeight="600"
                      color="blue.600"
                      bg="blue.50"
                      px={2}
                      py={0.5}
                      borderRadius="md"
                      textTransform="none"
                    >
                      somente leitura
                    </Badge>
                  </Flex>
                  <Text fontSize="12px" color="gray.400" mb={3}>
                    Espelho dos quadros gerenciados pelos módulos. Abra no módulo para editar.
                  </Text>
                  <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing={5}>
                    {projetosModulo.map(project => renderProjectCard(project, true))}
                  </SimpleGrid>
                </Box>
              )}
            </Flex>
          )}
        </Box>
      </Box>

      <KanbanProjectModal
        isOpen={isProjectModalOpen}
        onClose={onProjectModalClose}
        project={projectToEdit}
        columns={boardData?.data?.columns}
        onSave={handleSaveProject}
      />

      <DialogModal
        isOpen={isDeleteDialogOpen}
        status="warning"
        title="Arquivar projeto"
        description="Tem certeza que deseja arquivar este projeto? Todas as colunas e cards serao arquivados."
        onConfirm={confirmArchiveProject}
        onCancel={onDeleteDialogClose}
        confirmLabel="Arquivar"
        cancelLabel="Cancelar"
      />
    </PageBox>
  );
};
